import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: '四虎影院是否免费？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '四虎影院提供大量免费影视资源，部分VIP内容需要会员观看。我们致力于为用户提供优质的免费观影体验。',
      },
    },
    {
      '@type': 'Question',
      name: '四虎影院支持哪些设备？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '四虎影院支持电脑、平板、手机等多种终端设备，兼容主流浏览器，无需安装任何插件即可流畅观影。',
      },
    },
    {
      '@type': 'Question',
      name: '影片更新的频率如何？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '四虎影院每日更新最新影视资源，包括正在热播的电视剧、最新上映的电影、热门综艺和动漫，确保用户能第一时间观看到最新内容。',
      },
    },
    {
      '@type': 'Question',
      name: '如何搜索想看的影片？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '您可以通过网站顶部的搜索框输入影片名称进行搜索，也可以通过分类导航浏览不同类别的影视资源。',
      },
    },
    {
      '@type': 'Question',
      name: '播放卡顿怎么办？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '如果您遇到播放卡顿，建议检查网络连接、切换清晰度或尝试更换浏览器。如问题持续，请联系客服获取帮助。',
      },
    },
  ],
};

const aboutSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于四虎影院',
  description: '四虎影院品牌介绍、口碑评价与联系方式。了解四虎影院的品牌故事、用户口碑和客户服务。',
  url: 'https://jin-yuan.cn/about',
};

const stats = [
  { icon: 'ri-movie-2-line', value: '20,000+', label: '影视资源' },
  { icon: 'ri-user-star-line', value: '5,000万+', label: '活跃用户' },
  { icon: 'ri-thumb-up-line', value: '98.5%', label: '好评率' },
  { icon: 'ri-timer-line', value: '7x24', label: '客服支持' },
];

const testimonials = [
  {
    name: '张先生',
    avatar: 'https://readdy.ai/api/search-image?query=Professional%20Chinese%20male%20portrait%20headshot%2C%20clean%20white%20background%2C%20business%20casual%2C%20friendly%20expression&width=80&height=80&seq=avatar-01&orientation=squarish',
    text: '用了四虎影院好几年了，资源更新真的快，画质也很清晰。每天晚上回家看两集电视剧，已经成习惯了！',
    rating: 5,
  },
  {
    name: '李女士',
    avatar: 'https://readdy.ai/api/search-image?query=Professional%20Chinese%20female%20portrait%20headshot%2C%20clean%20white%20background%2C%20modern%20style%2C%20warm%20smile&width=80&height=80&seq=avatar-02&orientation=squarish',
    text: '分类做得很好，找片子特别方便。尤其是动漫区，国漫日漫都有，孩子也喜欢看。推荐给身边好多朋友了！',
    rating: 5,
  },
  {
    name: '王同学',
    avatar: 'https://readdy.ai/api/search-image?query=Young%20Chinese%20student%20portrait%20headshot%2C%20clean%20white%20background%2C%20casual%20style%2C%20bright%20expression&width=80&height=80&seq=avatar-03&orientation=squarish',
    text: '作为大学生，免费看高清电影真的很香。周末经常和室友一起追新番，观影体验完全不输付费平台。',
    rating: 4,
  },
];

export default function AboutPage() {
  const [activeTab, setActiveTab] = useState<'intro' | 'reputation' | 'contact'>('intro');

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(aboutSchema)}</script>
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-10">
          <div className="max-w-[1200px] mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />

            <div className="mt-4 mb-6">
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">关于我们</h1>
              <p className="text-sm text-foreground-500 mt-1">了解四虎影院的品牌故事与核心价值</p>
            </div>

            <div className="flex items-center gap-1 bg-background-100 rounded-full p-1 w-fit mb-8">
              {([
                { key: 'intro', label: '品牌介绍' },
                { key: 'reputation', label: '用户口碑' },
                { key: 'contact', label: '联系方式' },
              ] as const).map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`whitespace-nowrap px-5 py-2 rounded-full text-sm font-medium transition-all cursor-pointer ${
                    activeTab === tab.key
                      ? 'bg-background-50 text-foreground-950 shadow-sm'
                      : 'text-foreground-500 hover:text-foreground-700'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-10">
              {stats.map((stat) => (
                <div
                  key={stat.label}
                  className="p-5 bg-background-100 rounded-lg border border-background-200/50 text-center"
                >
                  <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-primary-500/15 mb-3">
                    <i className={`${stat.icon} text-primary-500 text-lg`}></i>
                  </div>
                  <div className="font-heading text-xl font-bold text-foreground-950">{stat.value}</div>
                  <div className="text-xs text-foreground-500 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>

            {activeTab === 'intro' && (
              <div className="space-y-8">
                <div className="bg-background-100 rounded-lg border border-background-200/50 p-6 md:p-8">
                  <h2 className="font-heading text-xl font-bold text-foreground-900 mb-4">品牌故事</h2>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    四虎影院成立于2018年，致力于为广大影视爱好者提供海量高清影视资源的在线观看服务。经过多年的发展，四虎影院已经成长为国内领先的在线影视平台之一，汇聚了电影、电视剧、综艺、动漫等丰富多样的影视内容。
                  </p>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    我们深知用户对高品质观影体验的追求，因此在资源质量、播放速度和用户体验方面持续投入。四虎影院的片库每日更新，确保用户能够第一时间观看到最新上映的热门影片和正在热播的电视剧。
                  </p>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    四虎影院的使命是"让每个人都能享受优质的影视内容"。无论您使用电脑、平板还是手机，四虎影院都能为您提供流畅、清晰、便捷的观影体验。我们相信，好的影视内容应该被更多人看到。
                  </p>
                </div>

                <div className="bg-background-100 rounded-lg border border-background-200/50 p-6 md:p-8">
                  <h2 className="font-heading text-xl font-bold text-foreground-900 mb-4">核心优势</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[
                      { icon: 'ri-shield-star-line', title: '海量资源', desc: '超过两万部高清影视资源，涵盖电影、电视剧、综艺、动漫等多个类别，满足不同用户的观影需求。' },
                      { icon: 'ri-speed-up-line', title: '极速播放', desc: '采用先进的CDN加速技术，确保影片加载速度快、播放流畅，无论何时何地都能畅快观影。' },
                      { icon: 'ri-smartphone-line', title: '多端兼容', desc: '完美适配电脑、平板、手机等多种终端设备，随时随地享受高清观影体验。' },
                      { icon: 'ri-calendar-check-line', title: '每日更新', desc: '片库每日更新，紧跟热播节奏，让您不错过任何一部精彩影片。' },
                    ].map((item) => (
                      <div key={item.title} className="flex gap-4 p-4 rounded-lg bg-background-50">
                        <div className="w-10 h-10 shrink-0 flex items-center justify-center rounded-lg bg-accent-500/15">
                          <i className={`${item.icon} text-accent-500 text-lg`}></i>
                        </div>
                        <div>
                          <h4 className="font-heading text-base font-bold text-foreground-900">{item.title}</h4>
                          <p className="text-sm text-foreground-500 mt-1">{item.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'reputation' && (
              <div className="space-y-6">
                <div className="bg-background-100 rounded-lg border border-background-200/50 p-6 md:p-8">
                  <h2 className="font-heading text-xl font-bold text-foreground-900 mb-6">用户评价</h2>
                  <div className="space-y-4">
                    {testimonials.map((t) => (
                      <div key={t.name} className="flex gap-4 p-4 rounded-lg bg-background-50">
                        <div className="w-12 h-12 shrink-0 rounded-full overflow-hidden bg-background-200">
                          <img src={t.avatar} alt={t.name} className="w-full h-full object-cover" loading="lazy" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1.5">
                            <span className="text-sm font-semibold text-foreground-900">{t.name}</span>
                            <div className="flex items-center gap-0.5">
                              {Array.from({ length: t.rating }).map((_, i) => (
                                <i key={i} className="ri-star-fill text-accent-500 text-xs"></i>
                              ))}
                            </div>
                          </div>
                          <p className="text-sm text-foreground-600 leading-relaxed">{t.text}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-background-100 rounded-lg border border-background-200/50 p-6 md:p-8">
                  <h2 className="font-heading text-xl font-bold text-foreground-900 mb-4">媒体评价</h2>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {[
                      { source: '影视周刊', quote: '四虎影院凭借丰富的片源和流畅的播放体验，已经成为国内用户首选的在线观影平台之一。' },
                      { source: '科技视界', quote: '在众多影视平台中，四虎影院以出色的用户体验和高清画质脱颖而出，值得推荐。' },
                      { source: '数字生活', quote: '界面简洁易用，分类清晰，资源更新及时，是追剧一族的理想选择。' },
                    ].map((m) => (
                      <div key={m.source} className="p-4 rounded-lg bg-background-50 border border-background-200/50">
                        <i className="ri-double-quotes-l text-2xl text-primary-500/30 mb-2 block"></i>
                        <p className="text-sm text-foreground-600 leading-relaxed mb-3">{m.quote}</p>
                        <span className="text-xs text-foreground-500 font-medium">—— {m.source}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'contact' && (
              <div className="space-y-6">
                <div className="bg-background-100 rounded-lg border border-background-200/50 p-6 md:p-8">
                  <h2 className="font-heading text-xl font-bold text-foreground-900 mb-6">联系我们</h2>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {[
                      { icon: 'ri-mail-line', title: '电子邮箱', value: 'contact@sihucinema.com', desc: '商务合作与媒体咨询' },
                      { icon: 'ri-phone-line', title: '客服电话', value: '400-888-6666', desc: '7x24小时客服热线' },
                      { icon: 'ri-map-pin-line', title: '公司地址', value: '北京市朝阳区建国路88号', desc: '欢迎来访洽谈合作' },
                    ].map((item) => (
                      <div key={item.title} className="text-center p-5 rounded-lg bg-background-50">
                        <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-primary-500/15 mb-3">
                          <i className={`${item.icon} text-primary-500 text-xl`}></i>
                        </div>
                        <h4 className="text-sm font-semibold text-foreground-900">{item.title}</h4>
                        <p className="text-sm text-foreground-700 font-medium mt-1">{item.value}</p>
                        <p className="text-xs text-foreground-500 mt-1">{item.desc}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-background-100 rounded-lg border border-background-200/50 p-6 md:p-8">
                  <h2 className="font-heading text-xl font-bold text-foreground-900 mb-6">常见问题</h2>
                  <div className="space-y-3">
                    {[
                      { q: '四虎影院是否免费？', a: '四虎影院提供大量免费影视资源，部分VIP内容需要会员观看。我们致力于为用户提供优质的免费观影体验。' },
                      { q: '四虎影院支持哪些设备？', a: '四虎影院支持电脑、平板、手机等多种终端设备，兼容主流浏览器，无需安装任何插件即可流畅观影。' },
                      { q: '影片更新的频率如何？', a: '四虎影院每日更新最新影视资源，包括正在热播的电视剧、最新上映的电影、热门综艺和动漫，确保用户能第一时间观看到最新内容。' },
                      { q: '如何搜索想看的影片？', a: '您可以通过网站顶部的搜索框输入影片名称进行搜索，也可以通过分类导航浏览不同类别的影视资源。' },
                      { q: '播放卡顿怎么办？', a: '如果您遇到播放卡顿，建议检查网络连接、切换清晰度或尝试更换浏览器。如问题持续，请联系客服获取帮助。' },
                    ].map((faq) => (
                      <details key={faq.q} className="group rounded-lg bg-background-50 border border-background-200/50">
                        <summary className="flex items-center justify-between px-5 py-4 cursor-pointer text-sm font-medium text-foreground-900 list-none">
                          {faq.q}
                          <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                        </summary>
                        <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">{faq.a}</div>
                      </details>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}